'use client';

import {
  computeQuoteTotals,
  effectiveGlobalAdjustment,
  quoteModeAllowsAdjustment,
  type QuoteModeValue,
} from '@devizy/shared';
import * as React from 'react';

import { cn } from '../../lib/cn';
import { Accordion } from '../accordion';
import { Badge } from '../badge';

import { autoQuoteDensity, type QuoteDensity } from './density';
import { QuoteDensityToggle } from './density-toggle';
import { applyLinePatch, buildAdditionLine, groupByParent } from './group-lines';
import { QuoteSection } from './quote-section';
import { QuoteSummary } from './quote-summary';

import type { QuoteViewLine, QuoteViewProps } from './types';

export function QuoteView({
  mode,
  labels,
  expectations,
  lines,
  quoteMode,
  currency,
  requesterCurrency,
  exchangeRate,
  offerPricingPreference,
  globalAdjustment = 0,
  density,
  onLinesChange,
  onQuoteModeChange,
  onGlobalAdjustmentChange,
  onDensityChange,
  className,
}: QuoteViewProps): React.ReactElement {
  const isEdit = mode === 'bidder-edit';

  const groups = React.useMemo(
    () => groupByParent(expectations, lines, labels.orphansGroup),
    [expectations, lines, labels.orphansGroup],
  );
  const [localDensity, setLocalDensity] = React.useState<QuoteDensity | null>(null);
  const densityOverride = density === undefined ? localDensity : density;
  const resolvedDensity = densityOverride ?? autoQuoteDensity(expectations);
  const appliedGlobalAdjustment = effectiveGlobalAdjustment(quoteMode, globalAdjustment);
  const totals = React.useMemo(
    () =>
      computeQuoteTotals(
        lines.map((l) => ({
          mode: l.mode,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          adjustment: l.adjustment,
          isProposedForDeletion: l.isProposedForDeletion,
        })),
        appliedGlobalAdjustment,
      ),
    [lines, appliedGlobalAdjustment],
  );

  const editable = isEdit && onLinesChange;
  const onLineChange = editable
    ? (line: QuoteViewLine, patch: Partial<QuoteViewLine>): void => {
        const idx = lines.findIndex((l) => (l.id ? l.id === line.id : l === line));
        if (idx >= 0) onLinesChange(applyLinePatch(lines, idx, patch, expectations, quoteMode));
      }
    : undefined;
  const onAddLine = editable
    ? (parentId: string): void =>
        onLinesChange([...lines, buildAdditionLine(lines, parentId, quoteMode)])
    : undefined;
  const onRemoveLine = editable
    ? (line: QuoteViewLine): void => {
        const idx = lines.findIndex((l) => (l.id ? l.id === line.id : l === line));
        if (idx >= 0) {
          onLinesChange(lines.filter((_, i) => i !== idx).map((l, order) => ({ ...l, order })));
        }
      }
    : undefined;

  function handleDensityChange(next: QuoteDensity): void {
    setLocalDensity(next);
    onDensityChange?.(next);
  }

  const densityToggle = (
    <QuoteDensityToggle value={resolvedDensity} onChange={handleDensityChange} labels={labels} />
  );

  const sections = groups.map((group) => (
    <QuoteSection
      key={group.parent.id}
      parent={group.parent}
      lines={group.lines}
      expectations={expectations}
      mode={mode}
      density={resolvedDensity}
      quoteMode={quoteMode}
      currency={currency}
      labels={labels}
      onLineChange={onLineChange}
      onAddLine={onAddLine}
      onRemoveLine={onRemoveLine}
    />
  ));

  const isOffPreference = Boolean(offerPricingPreference && offerPricingPreference !== quoteMode);
  const showModeWarning = isEdit && isOffPreference;
  const isCrossCurrency = Boolean(requesterCurrency && requesterCurrency !== currency);
  const indicativeTotal =
    isCrossCurrency && exchangeRate ? Math.ceil(totals.totalHt * exchangeRate * 100) / 100 : null;
  const conversionUnavailable = isCrossCurrency && exchangeRate === null;

  return (
    <div
      data-quote-view-mode={mode}
      data-quote-view-density={resolvedDensity}
      className={cn('bg-card text-card-foreground rounded-lg border p-4 text-sm', className)}
    >
      {isEdit ? (
        <div className="border-input mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
          <label className="text-muted-foreground flex items-center gap-2 text-xs">
            <span>{labels.modeLabel}</span>
            <select
              className="border-input bg-background rounded-md border px-2 py-1 text-sm"
              value={quoteMode}
              onChange={(e) => onQuoteModeChange?.(e.target.value as QuoteModeValue)}
            >
              {(Object.keys(labels.modes) as QuoteModeValue[]).map((m) => (
                <option key={m} value={m}>
                  {labels.modes[m]}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap items-center gap-3">
            {showModeWarning && offerPricingPreference ? (
              <p className="text-warning text-xs">
                {labels.modeWarning(labels.modes[offerPricingPreference])}
              </p>
            ) : null}
            {densityToggle}
          </div>
        </div>
      ) : (
        <div className="border-input mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-xs">
          <span>
            {labels.modeShortLabel}: {labels.modes[quoteMode]}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {isOffPreference && offerPricingPreference ? (
              <Badge
                variant="outline"
                className="border-warning/30 bg-warning-tint text-warning-strong"
                title={labels.offPreferenceHint(labels.modes[offerPricingPreference])}
                data-testid="quote-off-preference"
              >
                {labels.offPreference}
              </Badge>
            ) : null}
            {densityToggle}
          </div>
        </div>
      )}

      {resolvedDensity === 'flat' ? (
        <div className="space-y-2">{sections}</div>
      ) : (
        <Accordion type="multiple" className="space-y-2">
          {sections}
        </Accordion>
      )}

      <QuoteSummary
        subtotalHt={totals.subtotalHt}
        globalAdjustment={quoteModeAllowsAdjustment(quoteMode) ? globalAdjustment : null}
        totalHt={totals.totalHt}
        currency={currency}
        requesterCurrency={requesterCurrency}
        indicativeTotal={indicativeTotal}
        conversionUnavailable={conversionUnavailable}
        showIncompleteWarning={!totals.allLinesPriced && isEdit}
        editable={Boolean(
          isEdit && onGlobalAdjustmentChange && quoteModeAllowsAdjustment(quoteMode),
        )}
        onGlobalAdjustmentChange={onGlobalAdjustmentChange}
        labels={labels}
      />
    </div>
  );
}
